import { Slot } from '@radix-ui/react-slot';
import classNames from 'classnames';
import type { ComponentProps } from 'react';
import React, {
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import * as styles from './HoverView.module.scss';
import type { TetherRef } from './Tether';
import Tether from './Tether';
import { isTouchDevice } from '../constants/os';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import useControlledState from '../utils/useControlledState';
import type { HoverDelay } from '../utils/useHover';
import useHover from '../utils/useHover';

interface UseOpenManagerProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const useOpenManager = ({ open, onOpenChange }: UseOpenManagerProps) => {
  const [isOpen, setIsOpen] = useControlledState({
    defaultValue: false,
    value: open,
    onValueChange: onOpenChange,
  });
  const [hasBeenOpened, setHasBeenOpened] = useState(false);

  const onIsOpenChange = useCallback(
    (newIsOpen: boolean) => {
      setIsOpen(newIsOpen);
      if (newIsOpen) setHasBeenOpened(true);
    },
    [setIsOpen],
  );

  // Could happen when the component is controlled
  if (isOpen && !hasBeenOpened) {
    setHasBeenOpened(true);
  }

  return { isOpen, hasBeenOpened, onIsOpenChange };
};

const interactiveElementSelector =
  'button:not([disabled]), a[href], [role="button"], [tabindex]';

type HoverViewProps = Pick<
  ComponentProps<typeof Tether>,
  'placement' | 'flipBehavior' | 'offset'
> & {
  children: React.ReactNode;
  hoverGroupId?: string;
  hoverDelay?: HoverDelay;
  view: React.ReactNode;
} & (
    | {
        open: boolean;
        onOpenChange?: (open: boolean) => void;
      }
    | {
        open?: never;
        onOpenChange?: never;
      }
  );

/**
 * A headless component that displays a view when hovering over its children.
 * Both the view and the anchor (children) must be able to accept props and pass them
 * down to their underlying HTML elements, since they will receive refs, event handlers, etc.
 *
 * Following the product spec, when the anchor is not interactive, the view also appears
 * when the anchor is clicked.
 *
 * @example
 * <HoverView view={<UserCard userId="abc" />} placement="top">
 *   <div>Hover over me</div>
 * </HoverView>
 */
const HoverView = ({
  children,
  hoverGroupId = 'hoverView',
  hoverDelay = 'slow',
  view,
  open,
  onOpenChange,
  placement,
  flipBehavior,
  offset,
}: HoverViewProps) => {
  const id = useId();

  const { isOpen, hasBeenOpened, onIsOpenChange } = useOpenManager({
    open,
    onOpenChange,
  });

  const tetherRef = useRef<TetherRef>(null);
  useLayoutEffect(() => {
    if (isOpen) tetherRef.current?.updatePositionedView();
  }, [isOpen]);

  const {
    onMouseEnter: originalOnMouseEnter,
    onMouseLeave: originalOnMouseLeave,
    setHover,
  } = useHover({
    hoverGroupId,
    hoverDelay,
    onHoverChange(hoveringId) {
      onIsOpenChange(hoveringId === id);
    },
  });

  const onMouseEnter = isTouchDevice
    ? undefined
    : (e: React.MouseEvent<HTMLElement>) => {
        originalOnMouseEnter(id, e);
      };

  const onMouseLeave = isTouchDevice
    ? undefined
    : () => {
        originalOnMouseLeave(id);
      };

  const onClickOutside = (e: Event) => {
    if (
      !(e.target instanceof Node) ||
      !tetherRef.current?.anchorElement?.contains(e.target)
    ) {
      setHover(null);
    }
  };

  return (
    <Tether
      ref={tetherRef}
      positionedView={
        // Keep the view mounted in the DOM to prevent content recreation on re-display.
        // This is particularly beneficial for content with network requests.
        hasBeenOpened && (
          <ClickOutsideHandler
            asChild
            onClickOutside={isOpen ? onClickOutside : undefined}
          >
            <Slot
              aria-hidden={!isOpen}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              className={classNames(styles.floating, {
                [styles.isClosed]: !isOpen,
              })}
            >
              {view}
            </Slot>
          </ClickOutsideHandler>
        )
      }
      errorBoundary="HoverView"
      flipBehavior={flipBehavior}
      placement={placement}
      offset={offset}
    >
      <Slot
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={() => {
          const anchorElement = tetherRef.current?.anchorElement;
          if (!anchorElement) return;

          const isInteractive =
            !!anchorElement.querySelector(interactiveElementSelector) ||
            !!anchorElement.closest(interactiveElementSelector);

          // Only open the hover view if the element is not interactive,
          // to avoid conflicting with the element's default behavior
          if (!isInteractive) setHover(id);
        }}
      >
        {children}
      </Slot>
    </Tether>
  );
};

export default HoverView;
